// Reference data for the DTC & Status Byte Decoder (and, for DTC_STATUS_BITS, the
// UDS Diagnostic Session Simulator too — lib/uds re-exports it from here so both
// tools share one source of truth).
//
// Sources (recorded in the tool's user guide as well):
//   - DTC letter/digit structure and the P/U sub-group names: SAE J2012 (2002 edition,
//     published by Public.Resource.Org), sections 5.4-5.7 and Appendix B/C headings.
//   - DTC status bits: ISO 14229-1 DTCStatusMask definition.
//   - DTCFormatIdentifier values: ISO 14229-1 (0x19 0x01 / 0x19 0x42 responses).
//   - Failure Type Byte (FTB) categories and the small set of specific FTB names below:
//     SAE J2012-DA as transcribed in public references — a specific name is only given
//     where two independent public transcriptions agree (see FTB_NAMES).

export const hex2 = (n) => n.toString(16).toUpperCase().padStart(2, '0');
export const hex3 = (n) => n.toString(16).toUpperCase().padStart(3, '0');
export const bytesToHex = (bytes) => bytes.map(hex2).join(' ');

// ISO 14229-1 DTC status byte. `bit` is the mask value; `index` the bit position.
export const DTC_STATUS_BITS = [
  { index: 0, bit: 0x01, name: 'testFailed', short: 'TF', meaning: 'The test for this DTC failed the most recent time it ran.' },
  { index: 1, bit: 0x02, name: 'testFailedThisOperationCycle', short: 'TFTOC', meaning: 'The test has failed at least once during the current operation cycle.' },
  { index: 2, bit: 0x04, name: 'pendingDTC', short: 'PDTC', meaning: 'The test failed during the current or previous operation cycle, but is not yet confirmed.' },
  { index: 3, bit: 0x08, name: 'confirmedDTC', short: 'CDTC', meaning: 'The fault has failed enough times to be confirmed and stored in non-volatile memory.' },
  { index: 4, bit: 0x10, name: 'testNotCompletedSinceLastClear', short: 'TNCSLC', meaning: 'The test for this DTC has not run to completion since fault memory was last cleared.' },
  { index: 5, bit: 0x20, name: 'testFailedSinceLastClear', short: 'TFSLC', meaning: 'The test has failed at least once since fault memory was last cleared.' },
  { index: 6, bit: 0x40, name: 'testNotCompletedThisOperationCycle', short: 'TNCTOC', meaning: 'The test for this DTC has not run to completion during the current operation cycle.' },
  { index: 7, bit: 0x80, name: 'warningIndicatorRequested', short: 'WIR', meaning: 'The ECU is requesting the warning lamp/indicator be lit for this fault.' },
];

// --- DTC letter / digit structure (SAE J2012 section 5) ----------------------

export const DTC_LETTERS = {
  P: { bits: '00', name: 'Powertrain', covers: 'Engine, transmission, and hybrid/electric propulsion' },
  C: { bits: '01', name: 'Chassis', covers: 'Brakes/ABS, steering, suspension' },
  B: { bits: '10', name: 'Body', covers: 'Airbags, climate control, lighting, seats, comfort' },
  U: { bits: '11', name: 'Network', covers: 'Vehicle network communication (CAN, LIN, …) between modules' },
};

// Scope of the first digit, per letter. `scope` is 'generic' | 'manufacturer' |
// 'reserved' | 'split' (P3: depends on the 2nd digit, see describeDigit1 in dtc.js).
export const DTC_DIGIT1 = {
  P: {
    0: { scope: 'generic', label: 'ISO/SAE controlled (generic)' },
    1: { scope: 'manufacturer', label: 'Manufacturer controlled' },
    2: { scope: 'generic', label: 'ISO/SAE controlled (generic)' },
    3: { scope: 'split', label: 'P3000–P33FF manufacturer controlled; P3400–P3FFF ISO/SAE controlled/reserved' },
  },
  C: {
    0: { scope: 'generic', label: 'ISO/SAE controlled (generic)' },
    1: { scope: 'manufacturer', label: 'Manufacturer controlled' },
    2: { scope: 'manufacturer', label: 'Manufacturer controlled' },
    3: { scope: 'reserved', label: 'Reserved by SAE J2012' },
  },
  B: {
    0: { scope: 'generic', label: 'ISO/SAE controlled (generic)' },
    1: { scope: 'manufacturer', label: 'Manufacturer controlled' },
    2: { scope: 'manufacturer', label: 'Manufacturer controlled' },
    3: { scope: 'reserved', label: 'Reserved by SAE J2012' },
  },
  U: {
    0: { scope: 'generic', label: 'ISO/SAE controlled (generic)' },
    1: { scope: 'manufacturer', label: 'Manufacturer controlled' },
    2: { scope: 'manufacturer', label: 'Manufacturer controlled' },
    3: { scope: 'reserved', label: 'Reserved by SAE J2012' },
  },
};

// Sub-group (3rd character) names, keyed by the first two characters of the code.
// Only groupings the J2012 (2002) text actually lists are named; anything else is
// deliberately left undefined so the UI can say "no standard grouping".
const FUEL_AIR_AUX = 'Fuel and air metering and auxiliary emission controls';
export const DTC_SUBGROUPS = {
  P0: {
    0: FUEL_AIR_AUX,
    1: 'Fuel and air metering',
    2: 'Fuel and air metering (injector circuit)',
    3: 'Ignition system or misfire',
    4: 'Auxiliary emission controls',
    5: 'Vehicle speed, idle control and auxiliary inputs',
    6: 'Computer and auxiliary outputs',
    7: 'Transmission',
    8: 'Transmission',
    9: 'Transmission',
    A: 'Hybrid propulsion',
    B: 'Reserved in J2012-2002; later editions use P0B–P0F for hybrid/electric propulsion',
    C: 'Reserved in J2012-2002; later editions use P0B–P0F for hybrid/electric propulsion',
    D: 'Reserved in J2012-2002; later editions use P0B–P0F for hybrid/electric propulsion',
    E: 'Reserved in J2012-2002; later editions use P0B–P0F for hybrid/electric propulsion',
    F: 'Reserved in J2012-2002; later editions use P0B–P0F for hybrid/electric propulsion',
  },
  P1: {
    0: `${FUEL_AIR_AUX} (manufacturer controlled)`,
    1: 'Fuel and air metering (manufacturer controlled)',
    2: 'Fuel and air metering (manufacturer controlled)',
    3: 'Ignition system or misfire (manufacturer controlled)',
    4: 'Auxiliary emission controls (manufacturer controlled)',
    5: 'Vehicle speed, idle control and auxiliary inputs (manufacturer controlled)',
    6: 'Computer and auxiliary outputs (manufacturer controlled)',
    7: 'Transmission (manufacturer controlled)',
    8: 'Transmission (manufacturer controlled)',
    9: 'Transmission (manufacturer controlled)',
  },
  P2: {
    0: FUEL_AIR_AUX,
    1: FUEL_AIR_AUX,
    2: FUEL_AIR_AUX,
    3: 'Ignition system or misfire',
    4: 'Auxiliary emission controls',
    5: 'Auxiliary inputs',
    6: 'Computer and auxiliary outputs',
    7: 'Transmission',
    8: 'ISO/SAE reserved',
    A: FUEL_AIR_AUX,
  },
  P3: {
    0: `${FUEL_AIR_AUX} (manufacturer controlled)`,
    1: `${FUEL_AIR_AUX} (manufacturer controlled)`,
    2: `${FUEL_AIR_AUX} (manufacturer controlled)`,
    3: 'Ignition system or misfire (manufacturer controlled)',
    4: 'Cylinder deactivation',
    5: 'ISO/SAE reserved',
    6: 'ISO/SAE reserved',
    7: 'ISO/SAE reserved',
    8: 'ISO/SAE reserved',
    9: 'ISO/SAE reserved',
  },
  U0: {
    0: 'Network electrical',
    1: 'Network communication (lost communication with a module)',
    2: 'Network communication (lost communication with a module)',
    3: 'Network software',
    4: 'Network data (invalid data received from a module)',
  },
};

// --- Failure Type Byte (SAE J2012-DA, used as the 3rd byte of a UDS DTC) -------

// Category by high nibble.
export const FTB_CATEGORIES = {
  0x0: { name: 'General Failure Information', detail: 'No sub-type information, or a general category-level failure that has no more specific sub-type.' },
  0x1: { name: 'General Electrical Failures', detail: 'Circuit-level faults: shorts, opens, voltage/current/resistance out of range, intermittent.' },
  0x2: { name: 'General Signal Failures', detail: 'Signal amplitude, stuck, rate-of-change, bias, plausibility and erratic-signal faults.' },
  0x3: { name: 'FM (Frequency Modulated) / PWM Failures', detail: 'Pulse width, frequency and pulse-count faults on frequency-coded or PWM signals.' },
  0x4: { name: 'System Internal Failures', detail: 'Faults inside the control module itself: memory, checksum, watchdog, internal electronics, over-temperature.' },
  0x5: { name: 'System Programming Failures', detail: 'Module not programmed, not configured, missing calibration or incompatible software.' },
  0x6: { name: 'Algorithm Based Failures', detail: 'Faults found by software checks: signal calculation, comparison, plausibility, time-out, transition counts.' },
  0x7: { name: 'Mechanical Failures', detail: 'Actuator stuck/slipping, position not reachable, linkage, alignment, leaks and fluid level.' },
  0x8: { name: 'Bus Signal / Message Failures', detail: 'Invalid serial data, alive-counter or checksum errors, missing messages, bus-off.' },
  0x9: { name: 'Component Failures', detail: 'Component parameter, performance, operation, assembly, internal failure or operating-condition faults.' },
};

// Specific FTB names, given ONLY where two independent public J2012-DA-based
// transcriptions agree (piembsystech.com FTB table and autodtcs.com FTB article,
// both accessed 2026-09-26). Any other FTB shows its category only — never an
// invented meaning.
export const FTB_NAMES = {
  0x00: 'No sub-type information',
  0x11: 'Circuit short to ground',
  0x12: 'Circuit short to battery',
  0x13: 'Circuit open',
  0x14: 'Circuit short to ground or open',
  0x15: 'Circuit short to battery or open',
  0x16: 'Circuit voltage below threshold',
  0x17: 'Circuit voltage above threshold',
  0x2F: 'Signal erratic',
  0x62: 'Signal compare failure',
  0x96: 'Component internal failure',
};

// --- UDS 0x19 / 0x14 ----------------------------------------------------------

export const DTC_FORMAT_IDS = {
  0x00: { name: 'SAE_J2012-DA_DTCFormat_00', standard: 'ISO 15031-6 / SAE J2012-DA', detail: 'Two-byte OBD DTC (the P/C/B/U code) with the 3rd byte unused (0x00) — the legacy emissions format.' },
  0x01: { name: 'ISO_14229-1_DTCFormat', standard: 'ISO 14229-1', detail: '3-byte DTC whose meaning is defined by the vehicle manufacturer. Many OEMs still pack the first two bytes as a J2012 P/C/B/U code, but that is a convention, not a rule of this format.' },
  0x02: { name: 'SAE_J1939-73_DTCFormat', standard: 'SAE J1939-73', detail: '3-byte SPN + FMI format used on heavy-duty/J1939 networks. The P/C/B/U decoding shown by this tool does NOT apply.' },
  0x03: { name: 'ISO_11992-4_DTCFormat', standard: 'ISO 11992-4', detail: 'Truck/trailer interface DTC format. The P/C/B/U decoding shown by this tool does NOT apply.' },
  0x04: { name: 'SAE_J2012-DA_DTCFormat_04', standard: 'SAE J2012-DA', detail: '3-byte DTC: 2-byte P/C/B/U code plus a J2012-DA Failure Type Byte — exactly the decoding this tool shows.' },
};

export const UDS_19_SUBFUNCTIONS = {
  0x01: 'reportNumberOfDTCByStatusMask',
  0x02: 'reportDTCByStatusMask',
  0x03: 'reportDTCSnapshotIdentification',
  0x04: 'reportDTCSnapshotRecordByDTCNumber',
  0x05: 'reportDTCStoredDataByRecordNumber',
  0x06: 'reportDTCExtDataRecordByDTCNumber',
  0x07: 'reportNumberOfDTCBySeverityMaskRecord',
  0x08: 'reportDTCBySeverityMaskRecord',
  0x09: 'reportSeverityInformationOfDTC',
  0x0A: 'reportSupportedDTC',
  0x0B: 'reportFirstTestFailedDTC',
  0x0C: 'reportFirstConfirmedDTC',
  0x0D: 'reportMostRecentTestFailedDTC',
  0x0E: 'reportMostRecentConfirmedDTC',
  0x14: 'reportDTCFaultDetectionCounter',
  0x15: 'reportDTCWithPermanentStatus',
  0x42: 'reportWWHOBDDTCByMaskRecord',
  0x55: 'reportWWHOBDDTCWithPermanentStatus',
};

// Sub-functions whose positive response is: availabilityMask + n x (DTC[3] + status).
export const UDS_19_DTC_LIST_SUBFUNCTIONS = new Set([0x02, 0x0A, 0x0B, 0x0C, 0x0D, 0x0E, 0x15]);

// --- OBD-II on CAN (ISO 15765-4) addressing ----------------------------------

export const describeCanId = (id) => {
  if (id === 0x7df) return { role: 'request', text: '0x7DF — OBD functional (broadcast) request to all emissions ECUs' };
  if (id >= 0x7e0 && id <= 0x7e7) return { role: 'request', ecu: id - 0x7e0 + 1, text: `0x${hex3(id)} — physical request to OBD ECU #${id - 0x7e0 + 1} (it answers on 0x${hex3(id + 8)})` };
  if (id >= 0x7e8 && id <= 0x7ef) {
    const n = id - 0x7e8 + 1;
    const typical = n === 1 ? ' — usually the engine/powertrain ECU' : n === 2 ? ' — often the transmission ECU' : '';
    return { role: 'response', ecu: n, text: `0x${hex3(id)} — response from OBD ECU #${n}${typical} (its request ID is 0x${hex3(id - 8)})` };
  }
  if (id >= 0x18db0000 && id <= 0x18dbffff && (id & 0xff) === 0xf1) return { role: 'request', text: `0x${id.toString(16).toUpperCase()} — 29-bit OBD functional request from tester 0xF1` };
  if (id >= 0x18da0000 && id <= 0x18daffff) {
    const target = (id >> 8) & 0xff;
    const source = id & 0xff;
    if (target === 0xf1) return { role: 'response', text: `0x${id.toString(16).toUpperCase()} — 29-bit response from ECU address 0x${hex2(source)} to tester 0xF1` };
    if (source === 0xf1) return { role: 'request', text: `0x${id.toString(16).toUpperCase()} — 29-bit physical request from tester 0xF1 to ECU 0x${hex2(target)}` };
  }
  return { role: 'unknown', text: `0x${id.toString(16).toUpperCase()} — not a standard OBD diagnostic CAN ID; the addressing is OEM-defined` };
};
